import { useState, useMemo } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea,
} from 'recharts'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { colorTextAccent } from '@cloudscape-design/design-tokens'
import { useDesignTokens, SERIES_PALETTE } from '@/hooks/useDesignTokens'
import { downloadFile } from '@/engine/export'
import {
  applyRetentionPolicy,
  type TrendPoint,
} from '@/engine/scheduler-singleton'
import type { CallResult } from '@/types'

// ── Interfaces ──────────────────────────────────────────────────────────

export interface DashboardMetrics {
  avgTtft: number
  avgThroughput: number
  totalRuns: number
  successRate: number // 0-1
  timeRange: { from: string; to: string }
}

// ── Pure computation (exported for property tests) ──────────────────────

export function computeDashboardMetrics(results: CallResult[]): DashboardMetrics {
  if (results.length === 0) {
    return {
      avgTtft: 0,
      avgThroughput: 0,
      totalRuns: 0,
      successRate: 0,
      timeRange: { from: '', to: '' },
    }
  }

  const successful = results.filter((r) => r.success)
  const successRate = successful.length / results.length

  const avgTtft =
    successful.length > 0
      ? successful.reduce((sum, r) => sum + r.ttftSec, 0) / successful.length
      : 0

  const avgThroughput =
    successful.length > 0
      ? successful.reduce((sum, r) => sum + r.tokenThroughput, 0) / successful.length
      : 0

  // Count distinct run IDs - use profileId + timestamp combo as proxy when no runId
  const runIds = new Set<string>()
  for (const r of results) {
    // CallResult doesn't have runId directly, but we can group by timestamp rounded to second
    const key = (r as CallResult & { runId?: string }).runId
      ?? `${Math.floor(new Date(r.timestamp).getTime() / 1000)}`
    runIds.add(key)
  }

  const timestamps = results.map((r) => new Date(r.timestamp).getTime()).filter((t) => !isNaN(t))
  const from = timestamps.length > 0 ? new Date(Math.min(...timestamps)).toISOString() : ''
  const to = timestamps.length > 0 ? new Date(Math.max(...timestamps)).toISOString() : ''

  return {
    avgTtft,
    avgThroughput,
    totalRuns: runIds.size,
    successRate,
    timeRange: { from, to },
  }
}

// ── Constants ───────────────────────────────────────────────────────────

const TREND_RESULTS_KEY = 'bedrock-diag-trend-results'

const TIME_RANGES = [
  { label: '1h', ms: 60 * 60 * 1000 },
  { label: '6h', ms: 6 * 60 * 60 * 1000 },
  { label: '24h', ms: 24 * 60 * 60 * 1000 },
  { label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
  { label: 'All', ms: 0 },
] as const

type MetricKey = 'avgTtftSec' | 'avgThroughput' | 'avgTotalSec'

const METRIC_OPTIONS: { key: MetricKey; label: string }[] = [
  { key: 'avgTtftSec', label: 'TTFT (s)' },
  { key: 'avgThroughput', label: 'Throughput (tok/s)' },
  { key: 'avgTotalSec', label: 'Total Time (s)' },
]

// ── Extended trend point with execution mode ────────────────────────────

interface ExtendedTrendPoint extends TrendPoint {
  executionMode: 'cloud' | 'local'
}

// ── Helpers ─────────────────────────────────────────────────────────────

const RUNS_KEY = 'bedrock-diag-runs'

function loadTrendData(): ExtendedTrendPoint[] {
  const points: ExtendedTrendPoint[] = []

  // 1. Load from dedicated trend results (written by scheduler)
  try {
    const raw = localStorage.getItem(TREND_RESULTS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as TrendPoint[]
      // Scheduler runs are local mode
      points.push(...parsed.map(p => ({ ...p, executionMode: 'local' as const })))
    }
  } catch { /* ignore corrupt data */ }

  // 2. Convert past runs from bedrock-diag-runs into trend points
  try {
    const raw = localStorage.getItem(RUNS_KEY)
    if (raw) {
      const runs = JSON.parse(raw) as Array<{
        id: string
        endTime: string
        results?: Array<{ executionMode?: string }>
        stats?: Array<{
          profileName: string
          profileType: string
          avgTotalSec: number
          avgTtftSec: number
          avgThroughput: number
          p95TotalSec: number
          callCount: number
          successCount: number
        }>
      }>
      for (const run of runs) {
        if (!run.stats) continue
        const mode: 'cloud' | 'local' = run.results?.[0]?.executionMode === 'local' ? 'local' : 'cloud'
        for (const s of run.stats) {
          points.push({
            runId: run.id,
            timestamp: run.endTime,
            scheduled: false,
            profileName: s.profileName,
            profileType: s.profileType,
            avgTotalSec: s.avgTotalSec,
            avgTtftSec: s.avgTtftSec,
            avgThroughput: s.avgThroughput,
            p95TotalSec: s.p95TotalSec,
            callCount: s.callCount,
            successCount: s.successCount,
            executionMode: mode,
          })
        }
      }
    }
  } catch { /* ignore corrupt data */ }

  // Deduplicate by runId + profileName
  const seen = new Set<string>()
  const deduped: ExtendedTrendPoint[] = []
  for (const p of points) {
    const key = `${p.runId}|${p.profileName}`
    if (!seen.has(key)) {
      seen.add(key)
      deduped.push(p)
    }
  }

  return applyRetentionPolicy(deduped) as ExtendedTrendPoint[]
}

function exportTrendData(points: TrendPoint[]): void {
  const data = {
    exportedAt: new Date().toISOString(),
    pointCount: points.length,
    trends: points,
  }
  const ts = new Date().toISOString().replace(/[:.]/g, '-')
  downloadFile(JSON.stringify(data, null, 2), `trend-results-${ts}.json`, 'application/json')
}

// ── Component ───────────────────────────────────────────────────────────

export function DashboardSection() {
  const [timeRange, setTimeRange] = useState(0) // 0 = All
  const [metric, setMetric] = useState<MetricKey>('avgTtftSec')
  const [chartType, setChartType] = useState<'line' | 'bar'>('line')
  const [modeFilter, setModeFilter] = useState<Set<'cloud' | 'local'>>(new Set(['cloud', 'local']))
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string> | null>(null) // null = all
  const [zoomLeft, setZoomLeft] = useState<number | null>(null)
  const [zoomRight, setZoomRight] = useState<number | null>(null)
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null)

  const tokens = useDesignTokens()

  // Load trend data
  const trendPoints = useMemo(() => loadTrendData(), [])

  // All unique profile names in the data (for the filter)
  const allProfileNames = useMemo(
    () => [...new Set(trendPoints.map(p => p.profileName))].sort(),
    [trendPoints]
  )

  // Initialize selectedProfiles to all on first load
  const activeProfiles = selectedProfiles ?? new Set(allProfileNames)

  // Apply all filters: time range, execution mode, profile
  const now = Date.now()
  const filteredPoints = useMemo(() => {
    return trendPoints.filter(p => {
      if (timeRange > 0 && now - new Date(p.timestamp).getTime() >= timeRange) return false
      if (!modeFilter.has(p.executionMode)) return false
      if (!activeProfiles.has(p.profileName)) return false
      return true
    })
  }, [trendPoints, timeRange, now, modeFilter, activeProfiles])

  // Aggregated metrics from trend points
  const metrics = useMemo((): DashboardMetrics => {
    if (filteredPoints.length === 0) {
      return { avgTtft: 0, avgThroughput: 0, totalRuns: 0, successRate: 0, timeRange: { from: '', to: '' } }
    }

    const totalCalls = filteredPoints.reduce((s, p) => s + p.callCount, 0)
    const totalSuccess = filteredPoints.reduce((s, p) => s + p.successCount, 0)
    const avgTtft =
      filteredPoints.reduce((s, p) => s + p.avgTtftSec * p.callCount, 0) / totalCalls
    const avgThroughput =
      filteredPoints.reduce((s, p) => s + p.avgThroughput * p.callCount, 0) / totalCalls
    const runIds = new Set(filteredPoints.map((p) => p.runId))
    const timestamps = filteredPoints.map((p) => new Date(p.timestamp).getTime())
    const from = new Date(Math.min(...timestamps)).toISOString()
    const to = new Date(Math.max(...timestamps)).toISOString()

    return {
      avgTtft,
      avgThroughput,
      totalRuns: runIds.size,
      successRate: totalCalls > 0 ? totalSuccess / totalCalls : 0,
      timeRange: { from, to },
    }
  }, [filteredPoints])

  // Build chart data: group by timestamp, one key per profile
  const profileNames = useMemo(
    () => [...new Set(filteredPoints.map((p) => p.profileName))],
    [filteredPoints]
  )

  const allChartData = useMemo(() => {
    const byEpoch = new Map<number, Record<string, unknown>>()
    for (const t of filteredPoints) {
      const epoch = new Date(t.timestamp).getTime()
      if (!byEpoch.has(epoch)) byEpoch.set(epoch, { epoch })
      byEpoch.get(epoch)![t.profileName] = t[metric]
    }
    return Array.from(byEpoch.values()).sort(
      (a, b) => (a.epoch as number) - (b.epoch as number)
    )
  }, [filteredPoints, metric])

  // Apply zoom domain
  const chartData = useMemo(() => {
    if (!zoomDomain) return allChartData
    return allChartData.filter(
      (d) =>
        (d.epoch as number) >= zoomDomain[0] &&
        (d.epoch as number) <= zoomDomain[1]
    )
  }, [allChartData, zoomDomain])

  const visibleProfiles = useMemo(
    () => profileNames.filter((name) => chartData.some((d) => d[name] !== undefined)),
    [profileNames, chartData]
  )

  // ── Zoom handlers ──
  const handleMouseDown = (e: any) => {
    if (e?.activeLabel) setZoomLeft(e.activeLabel)
  }
  const handleMouseMove = (e: any) => {
    if (zoomLeft && e?.activeLabel) setZoomRight(e.activeLabel)
  }
  const handleMouseUp = () => {
    if (zoomLeft && zoomRight && zoomLeft !== zoomRight) {
      const [l, r] =
        zoomLeft < zoomRight ? [zoomLeft, zoomRight] : [zoomRight, zoomLeft]
      setZoomDomain([l, r])
    }
    setZoomLeft(null)
    setZoomRight(null)
  }
  const resetZoom = () => setZoomDomain(null)

  const formatTick = (epoch: number) => {
    const d = new Date(epoch)
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <SpaceBetween size="l">
      {/* Aggregated Metrics Cards */}
      <Container header={<Header variant="h2">Aggregated Metrics</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Avg TTFT</Box>
            <Box variant="awsui-value-large">
              {metrics.avgTtft > 0 ? `${metrics.avgTtft.toFixed(3)}s` : '-'}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Avg Throughput</Box>
            <Box variant="awsui-value-large">
              {metrics.avgThroughput > 0
                ? `${metrics.avgThroughput.toFixed(1)} tok/s`
                : '-'}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Total Runs</Box>
            <Box variant="awsui-value-large">{metrics.totalRuns}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Success Rate</Box>
            <Box variant="awsui-value-large">
              {metrics.totalRuns > 0
                ? `${(metrics.successRate * 100).toFixed(1)}%`
                : '-'}
            </Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Trend Charts */}
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {zoomDomain && (
                  <Button variant="normal" onClick={resetZoom}>
                    Reset Zoom
                  </Button>
                )}
                <Button
                  variant="normal"
                  iconName="download"
                  onClick={() => exportTrendData(trendPoints)}
                  disabled={trendPoints.length === 0}
                >
                  Export
                </Button>
              </SpaceBetween>
            }
          >
            Latency Trends
          </Header>
        }
      >
        <SpaceBetween size="s">
          {/* Time range selectors */}
          <SpaceBetween direction="horizontal" size="xs">
            <Box variant="small" color="text-body-secondary">
              Range:
            </Box>
            {TIME_RANGES.map((r) => (
              <Button
                key={r.label}
                variant={timeRange === r.ms ? 'primary' : 'normal'}
                onClick={() => {
                  setTimeRange(r.ms)
                  setZoomDomain(null)
                }}
              >
                {r.label}
              </Button>
            ))}
          </SpaceBetween>

          {/* Mode filter */}
          <SpaceBetween direction="horizontal" size="xs">
            <Box variant="small" color="text-body-secondary">
              Mode:
            </Box>
            {(['cloud', 'local'] as const).map((mode) => (
              <Button
                key={mode}
                variant={modeFilter.has(mode) ? 'primary' : 'normal'}
                onClick={() => {
                  const next = new Set(modeFilter)
                  if (next.has(mode)) {
                    if (next.size > 1) next.delete(mode) // keep at least one
                  } else {
                    next.add(mode)
                  }
                  setModeFilter(next)
                }}
              >
                {mode === 'cloud' ? 'Cloud' : 'Local'}
              </Button>
            ))}
          </SpaceBetween>

          {/* Profile filter - only show if there are profiles in the data */}
          {allProfileNames.length > 0 && (
            <SpaceBetween direction="horizontal" size="xs">
              <Box variant="small" color="text-body-secondary">
                Models:
              </Box>
              <Button
                variant={activeProfiles.size === allProfileNames.length ? 'primary' : 'normal'}
                onClick={() => setSelectedProfiles(null)}
              >
                All
              </Button>
              {allProfileNames.map((name) => (
                <Button
                  key={name}
                  variant={activeProfiles.has(name) && activeProfiles.size < allProfileNames.length ? 'primary' : 'normal'}
                  onClick={() => {
                    const current = new Set(activeProfiles)
                    if (current.has(name) && current.size > 1) {
                      current.delete(name)
                    } else if (!current.has(name)) {
                      current.add(name)
                    }
                    setSelectedProfiles(current)
                  }}
                >
                  {name}
                </Button>
              ))}
            </SpaceBetween>
          )}

          {/* Metric selectors */}
          <SpaceBetween direction="horizontal" size="xs">
            <Box variant="small" color="text-body-secondary">
              Metric:
            </Box>
            {METRIC_OPTIONS.map((m) => (
              <Button
                key={m.key}
                variant={metric === m.key ? 'primary' : 'normal'}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </Button>
            ))}
            <Box variant="small" color="text-body-secondary" padding={{ left: 's' }}>
              Chart:
            </Box>
            <Button
              variant={chartType === 'line' ? 'primary' : 'normal'}
              onClick={() => setChartType('line')}
              iconName="status-positive"
            >
              Line
            </Button>
            <Button
              variant={chartType === 'bar' ? 'primary' : 'normal'}
              onClick={() => setChartType('bar')}
              iconName="menu"
            >
              Bar
            </Button>
          </SpaceBetween>

          {chartData.length > 0 ? (
            <>
              {chartType === 'line' && (
                <Box variant="small" color="text-body-secondary">
                  Drag on the chart to zoom in. {chartData.length} data points.
                </Box>
              )}
              {chartType === 'bar' && (
                <Box variant="small" color="text-body-secondary">
                  {chartData.length} data points.
                </Box>
              )}
              <ResponsiveContainer width="100%" height={420}>
                {chartType === 'line' ? (
                  <LineChart
                    data={chartData}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.gridColor} />
                    <XAxis
                      dataKey="epoch" type="number" scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={formatTick}
                      tick={{ fill: tokens.axisLabelColor, fontSize: 11 }}
                    />
                    <YAxis tick={{ fill: tokens.axisLabelColor, fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}`, borderRadius: 6 }}
                      labelFormatter={(epoch: number) => new Date(epoch).toLocaleString()}
                    />
                    <Legend />
                    {visibleProfiles.map((name, i) => (
                      <Line key={name} type="monotone" dataKey={name}
                        stroke={SERIES_PALETTE[i % SERIES_PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
                    ))}
                    {zoomLeft && zoomRight && (
                      <ReferenceArea x1={zoomLeft} x2={zoomRight}
                        strokeOpacity={0.3} fill={colorTextAccent} fillOpacity={0.15} />
                    )}
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.gridColor} />
                    <XAxis
                      dataKey="epoch" type="number" scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={formatTick}
                      tick={{ fill: tokens.axisLabelColor, fontSize: 11 }}
                    />
                    <YAxis tick={{ fill: tokens.axisLabelColor, fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}`, borderRadius: 6 }}
                      labelFormatter={(epoch: number) => new Date(epoch).toLocaleString()}
                    />
                    <Legend />
                    {visibleProfiles.map((name, i) => (
                      <Bar key={name} dataKey={name}
                        fill={SERIES_PALETTE[i % SERIES_PALETTE.length]} />
                    ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </>
          ) : (
            <Box
              textAlign="center"
              padding={{ vertical: 'xxl' }}
              color="text-body-secondary"
            >
              No trend data available for the selected time range. Run a
              benchmark to see trends here.
            </Box>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )
}
